import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";
import { resolveUserActiveQueryParam } from "../utils/listActiveFilter";

export type UserRoleCode = "tenant_admin" | "manager" | "office_staff" | "instructor" | "student";

export const ROLE_LABELS: Record<UserRoleCode, string> = {
  tenant_admin: "Kurum Yöneticisi",
  manager: "Şube Müdürü",
  office_staff: "Büro Personeli",
  instructor: "Eğitmen",
  student: "Kursiyer",
};

/** Kullanıcı oluştururken rol seçiminde gösterilir. */
export const ROLE_HINTS: Record<UserRoleCode, string> = {
  tenant_admin: "Tüm şubeleri ve tüm kayıtları görür. Şube ataması yalnızca varsayılan bağlam içindir; veriyi filtrelemez.",
  manager: "Yalnızca atandığı şubenin kursiyer, eğitmen, araç ve randevularını görür. Büro personeli de yönetebilir.",
  office_staff:
    "Şube müdürü ile aynı operasyonel yetkiler; şube müdürü açamaz ve müdür hesabını yönetemez. Eğitmen ve kursiyer yönetebilir.",
  instructor: "Kendi ders programı ve öğrencileri.",
  student: "Kendi randevuları (mobil panel).",
};

export type UserRow = {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  branch_name: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  is_active: boolean;
  role: UserRoleCode | null;
  created_at: string;
  updated_at: string;
};

export type UserListResponse = {
  rows: UserRow[];
  total: number;
  limit: number;
  offset: number;
};

export type UserFilter = {
  role?: UserRoleCode | "";
  branchId?: string | "";
  isActive?: "" | "true" | "false";
  q?: string;
  limit?: number;
  offset?: number;
};

export type CreateUserInput = {
  fullName: string;
  email: string;
  phone?: string | null;
  branchId?: string | null;
  role: UserRoleCode;
  password: string;
};

export type UpdateUserInput = {
  fullName: string;
  phone?: string | null;
  branchId?: string | null;
  email?: string;
};

function buildQuery(filter: UserFilter): Record<string, string> {
  const q: Record<string, string> = {};
  if (filter.role) q.role = filter.role;
  if (filter.branchId) q.branch_id = filter.branchId;
  const isActive = resolveUserActiveQueryParam(filter.isActive);
  if (isActive !== undefined) q.is_active = isActive;
  if (filter.q) q.q = filter.q;
  if (filter.limit !== undefined) q.limit = String(filter.limit);
  if (filter.offset !== undefined) q.offset = String(filter.offset);
  return q;
}

export function useUserList(filter: UserFilter): {
  data: UserListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<UserListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const queryKey = JSON.stringify(buildQuery(filter));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<UserListResponse>("/users", buildQuery(filter), { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Kullanıcı Listesi Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

export async function createUser(input: CreateUserInput): Promise<UserRow> {
  return api.post<UserRow>("/users", input);
}

export async function updateUser(id: string, input: UpdateUserInput): Promise<UserRow> {
  return api.put<UserRow>(`/users/${id}`, input);
}

export async function changeUserRole(id: string, role: UserRoleCode): Promise<UserRow> {
  return api.post<UserRow>(`/users/${id}/role`, { role });
}

export async function deleteUser(id: string): Promise<{ ok: true }> {
  return api.delete<{ ok: true }>(`/users/${id}`);
}

export async function setUserActive(id: string, isActive: boolean): Promise<UserRow> {
  return api.post<UserRow>(`/users/${id}/active`, { isActive });
}

export async function resetUserPassword(id: string, newPassword: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/users/${id}/reset-password`, { newPassword });
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return api.post<{ ok: true }>("/users/me/change-password", {
    currentPassword,
    newPassword,
  });
}
